import { authenticatedFetch, getAuthToken } from './auth'
import { API_BASE_URL, API_ENDPOINTS } from './config'
import { generateHashFromArrayBuffer } from './generateFileHash'
import { createCoalescedFetcher } from './requestCoalesce'

/**
 * Maps backend error messages to user-friendly messages
 */
function getUserFriendlyErrorMessage(backendError: string, statusCode?: number): string {
  // Handle specific backend error messages
  if (backendError.includes('Resume extraction not completed')) {
    return 'Please wait for extraction to finish before starting the interview.'
  }
  
  if (backendError.includes('No skills extracted from resume')) {
    return 'No skills found in your resume. Please upload a resume with technical skills.'
  }
  
  if (backendError.includes('Could not prioritize skills')) {
    return 'Unable to process your skills. Please try again.'
  }
  
  if (backendError.includes('match available topics') || backendError.includes('contact support')) {
    return 'Your skills don\'t match available interview topics. Please try uploading a different resume.'
  }
  
  if (backendError.includes('interview in progress')) {
    return 'You already have an interview in progress. Please complete it first.'
  }
  
  if (backendError.includes('Failed to start interview attempt')) {
    return 'Unable to start interview. Please try again.'
  }
  
  if (backendError.includes('Failed to start interview')) {
    return 'Unable to start interview. Please try again.'
  }
  
  if (backendError.includes('No questions available') || backendError.includes('questions are not available')) {
    // Check for specific error patterns
    if (backendError.includes('No questions available for any role')) {
      return 'No questions are available for your skills and experience level with any role. Please try selecting a different role or contact support.'
    }
    if (backendError.includes('No questions available for the mapped topics')) {
      return 'No questions are available for this combination of topics, role, and experience level. Please try a different role or experience level.'
    }
    return 'Questions are not available for this combination at the moment. Please try again after some time.'
  }
  
  if (backendError.includes('Resume not found') || backendError.includes('access denied')) {
    return 'Resume not found. Please upload your resume again.'
  }
  
  if (backendError.includes('Extraction not found')) {
    return 'Extraction data not found. Please try uploading again.'
  }

  if (backendError.includes('email in your resume does not match')) {
    return 'The email in your resume does not match your registered email. Please sign in with the email in your resume or update your resume and try again.'
  }

  // Resume file content validation errors — already user-friendly, pass through as-is
  if (
    backendError.includes('too small to be a resume') ||
    backendError.includes('does not appear to be a valid PDF') ||
    backendError.includes('password-protected') ||
    backendError.includes('image-based') ||
    backendError.includes('may be corrupted') ||
    backendError.includes('no readable text') ||
    backendError.includes('very little text') ||
    backendError.includes('valid Word document')
  ) {
    return backendError
  }

  // Generic fallbacks based on status code
  if (statusCode === 400 || statusCode === 422) {
    return 'Invalid request. Please check your data and try again.'
  }
  
  if (statusCode === 404) {
    return 'Resource not found. Please try again.'
  }
  
  if (statusCode === 409) {
    return 'A conflict occurred. Please try again.'
  }
  
  if (statusCode === 500 || statusCode === 502 || statusCode === 503) {
    return 'Something went wrong. Please try again later.'
  }
  
  // Default fallback
  return 'An error occurred. Please try again.'
}

/**
 * Returns a user-friendly message for resume extraction errors.
 * Hides technical details (HTTP status codes, exception names, etc.) from the UI.
 */
export function getFriendlyExtractionErrorMessage(backendMessage: string | null | undefined): string {
  if (!backendMessage || !backendMessage.trim()) {
    return 'Resume analysis failed. Please try again.'
  }
  const msg = backendMessage.trim()
  // Replace technical messages so we never show HTTP codes or stack traces in the UI
  if (/\bHTTP\s*\d{3}\b/i.test(msg) || msg.includes('status_code') || msg.includes('TimeoutException') ||
      msg.includes('Request error') || msg.includes('Error starting extraction') ||
      msg.includes('timed out after') || msg.includes('No external resume ID') ||
      msg.includes('service not configured') || msg.includes('Resume analyzer service URL')) {
    return 'Resume analysis failed. Please try again.'
  }
  if (msg.includes('taking longer than expected') || msg.includes('try again later')) {
    return msg
  }
  return msg
}

export interface UploadResumeResponse {
  file_name: string
  message: string
}

export interface SeedResumeTestModeResponse {
  file_name: string
  message: string
  test_mode: boolean
}

/**
 * Development only: create a completed fake resume + extraction (no upload). Backend must have ENABLE_RESUME_TEST_MODE.
 */
export async function seedResumeTestMode(): Promise<SeedResumeTestModeResponse> {
  const response = await authenticatedFetch(`${API_BASE_URL}${API_ENDPOINTS.RESUME_TEST_MODE}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  })

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Session expired. Please sign in again.')
    }
    if (response.status === 404) {
      throw new Error('Resume test mode is not enabled on the server. Set ENABLE_RESUME_TEST_MODE in the API environment.')
    }
    if (response.status >= 500 && response.status < 600) {
      throw new Error('Something went wrong. Please try again later.')
    }
    const errorData = await response.json().catch(() => ({ detail: 'Request failed' }))
    const backendError =
      typeof errorData.detail === 'string' ? errorData.detail : `Request failed: ${response.status}`
    throw new Error(getUserFriendlyErrorMessage(backendError, response.status))
  }

  return await response.json()
}

/**
 * Thrown when the backend could not detect an email in the resume and asks for confirmation
 * (HTTP 422 Unprocessable Content with code EMAIL_MISSING_CONFIRMATION, or legacy 400).
 * The UI may auto-retry with confirmMissingEmail: true or show a confirmation modal.
 */
export class EmailMissingConfirmationError extends Error {
  constructor(message = 'We could not detect any email in your resume. Are you sure you want to continue without an email?') {
    super(message)
    this.name = 'EmailMissingConfirmationError'
  }
}

export interface ResumeExtractionResponse {
  technical_skills?: string[]
  position?: string | null
  total_experience_years?: number
  file_name?: string
  file_type?: string
  file_size?: number
  extracted_at?: string
  extraction_status: 'pending' | 'processing' | 'completed' | 'failed'
  error_message?: string
}

export interface MyResumeResponse {
  /** Present on federated API; false means no resume uploaded (GET still returns 200). */
  has_resume?: boolean
  file_name: string
  file_type?: string
  file_size?: number
  created_at?: string | null
  extracted_at?: string | null
  extraction_status: 'pending' | 'processing' | 'completed' | 'failed'
  skills?: any
  education?: any[]
  total_experience_years?: number
  companies?: any[]
  extraction_logs?: any[]
  error_message?: string
}

export interface UploadResumeOptions {
  /** When true, backend skips "email missing" validation (used after user confirms in modal). */
  confirmMissingEmail?: boolean
}

/**
 * Upload a resume file to the backend
 */
export async function uploadResume(file: File, options?: UploadResumeOptions): Promise<UploadResumeResponse> {
  const formData = new FormData()
  formData.append('file', file)
  if (options?.confirmMissingEmail === true) {
    formData.append('confirm_missing_email', 'true')
  }

  const response = await authenticatedFetch(`${API_BASE_URL}/resumes/upload`, {
    method: 'POST',
    body: formData
  })

  if (!response.ok) {
    // Handle 401 - token expired
    if (response.status === 401) {
      throw new Error('Session expired. Please sign in again.')
    }

    // For server errors (500, 502, 503, 504), show user-friendly message
    if (response.status >= 500 && response.status < 600) {
      throw new Error('Something went wrong. Please try again later.')
    }

    const errorData = await response.json().catch(() => ({ detail: 'Upload failed' }))
    const rawDetail = errorData.detail
    const code = typeof rawDetail === 'object' && rawDetail !== null && 'code' in rawDetail
      ? (rawDetail as { code: string }).code
      : null
    const messageRaw = typeof rawDetail === 'object' && rawDetail !== null && 'detail' in rawDetail
      ? (rawDetail as { detail: string }).detail
      : (typeof rawDetail === 'string' ? rawDetail : `Upload failed: ${response.status}`)
    const message = typeof messageRaw === 'string' && messageRaw.trim() ? messageRaw : `Upload failed: ${response.status}`

    if (code === 'EMAIL_MISSING_CONFIRMATION') {
      throw new EmailMissingConfirmationError(message)
    }
    if (
      message.toLowerCase().includes('could not detect any email') ||
      message.toLowerCase().includes('continue without an email')
    ) {
      throw new EmailMissingConfirmationError(message)
    }
    if (code === 'EMAIL_MISMATCH') {
      throw new Error(message)
    }
    throw new Error(getUserFriendlyErrorMessage(message, response.status))
  }

  return await response.json()
}

/**
 * Upload a resume and block until extraction reaches a terminal state.
 * Response shape matches GET /resumes/me/extraction.
 */
export async function uploadResumeAndExtract(file: File, options?: UploadResumeOptions): Promise<ResumeExtractionResponse> {
  const formData = new FormData()
  formData.append('file', file)
  if (options?.confirmMissingEmail === true) {
    formData.append('confirm_missing_email', 'true')
  }

  const response = await authenticatedFetch(`${API_BASE_URL}/resumes/upload-and-extract`, {
    method: 'POST',
    body: formData
  })

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Session expired. Please sign in again.')
    }

    if (response.status >= 500 && response.status < 600) {
      throw new Error('Something went wrong. Please try again later.')
    }

    const errorData = await response.json().catch(() => ({ detail: 'Upload failed' }))
    const rawDetail = errorData.detail
    const code = typeof rawDetail === 'object' && rawDetail !== null && 'code' in rawDetail
      ? (rawDetail as { code: string }).code
      : null
    const messageRaw = typeof rawDetail === 'object' && rawDetail !== null && 'detail' in rawDetail
      ? (rawDetail as { detail: string }).detail
      : (typeof rawDetail === 'string' ? rawDetail : `Upload failed: ${response.status}`)
    const message = typeof messageRaw === 'string' && messageRaw.trim() ? messageRaw : `Upload failed: ${response.status}`

    if (code === 'EMAIL_MISSING_CONFIRMATION') {
      throw new EmailMissingConfirmationError(message)
    }
    if (
      message.toLowerCase().includes('could not detect any email') ||
      message.toLowerCase().includes('continue without an email')
    ) {
      throw new EmailMissingConfirmationError(message)
    }
    if (code === 'EMAIL_MISMATCH') {
      throw new Error(message)
    }
    throw new Error(getUserFriendlyErrorMessage(message, response.status))
  }

  return await response.json()
}

const _myResumeFetcher = createCoalescedFetcher<MyResumeResponse | null>(0)

/**
 * Get current user's resume information.
 * Returns null when the user has no resume (200 + has_resume=false, or legacy 404).
 * Coalesces concurrent duplicate calls; no caching so post-upload refetches stay fresh.
 */
export function getMyResume(): Promise<MyResumeResponse | null> {
  return _myResumeFetcher('me', _getMyResumeImpl)
}

async function _getMyResumeImpl(): Promise<MyResumeResponse | null> {
  const response = await authenticatedFetch(`${API_BASE_URL}/resumes/me`)

  if (!response.ok) {
    // Legacy API: no resume was returned as 404
    if (response.status === 404) {
      return null
    }

    // Handle 401 - token expired
    if (response.status === 401) {
      throw new Error('Session expired. Please sign in again.')
    }

    // For server errors, show user-friendly message
    if (response.status >= 500 && response.status < 600) {
      throw new Error('Something went wrong. Please try again later.')
    }

    const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch resume' }))
    const backendError = errorData.detail || `Failed to fetch resume: ${response.status}`
    throw new Error(getUserFriendlyErrorMessage(backendError, response.status))
  }

  const data = (await response.json()) as MyResumeResponse
  if (data.has_resume === false) {
    return null
  }
  const { has_resume: _has, ...rest } = data
  return rest as MyResumeResponse
}

/**
 * True if any recruiter JD assignment has a stored resume_url for this user (candidate_jd_attachments).
 * Used to skip the JD resume gate when GET /resumes/me returns null for recruiter-added candidates.
 */
export async function hasRecruiterProvidedResume(): Promise<boolean> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}${API_ENDPOINTS.HAS_RECRUITER_PROVIDED_RESUME}`
  )
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Session expired. Please sign in again.')
    }
    return false
  }
  const data = (await response.json()) as { has_recruiter_resume?: boolean }
  return data.has_recruiter_resume === true
}

/**
 * Get resume extraction results for the current user's resume
 */
export async function getResumeExtraction(): Promise<ResumeExtractionResponse> {
  const response = await authenticatedFetch(`${API_BASE_URL}/resumes/me/extraction`)

  if (!response.ok) {
    // Handle 401 - token expired
    if (response.status === 401) {
      throw new Error('Session expired. Please sign in again.')
    }

    // For server errors, show user-friendly message
    if (response.status >= 500 && response.status < 600) {
      throw new Error('Something went wrong. Please try again later.')
    }

    const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch extraction' }))
    const backendError = errorData.detail || `Failed to fetch extraction: ${response.status}`
    throw new Error(getUserFriendlyErrorMessage(backendError, response.status))
  }

  return await response.json()
}

export interface ResumeMapRequest {
  resume_id?: string  // Optional; omit to use current user's resume
  skills: string[]
  position: string | null
  experience_years: number | null
}

export interface ResumeMapResponse {
  success: boolean
  mapped_topics: string[]
  role: string
  experience: string
  role_matched: boolean
  experience_matched: boolean
  role_warning?: string | null
  experience_warning?: string | null
}

export interface ResumeSaveRequest {
  resume_id?: string  // Optional; omit to use current user's resume
  // Option 1: Raw data (backward compatible)
  skills?: string[]
  position?: string | null
  experience_years?: number | null
  // Option 2: Pre-mapped data (new)
  mapped_topics?: string[]
  mapped_role?: string
  mapped_experience?: string
  // Option 3: Option ID (new - for selected interview option)
  option_id?: string
}

export interface ResumeSaveResponse {
  success: boolean
  interview_id: string
  role: string
  experience: string
  role_matched?: boolean
  experience_matched?: boolean
  role_warning?: string | null
  experience_warning?: string | null
}

/**
 * Error thrown when payment is required to proceed.
 * Contains product details for the payment modal.
 */
export class PaymentRequiredError extends Error {
  productId: string
  productPrice: number
  productName: string

  constructor(productId: string, productPrice: number, productName: string, message = 'Payment is required to access Skill Assessment.') {
    super(message)
    this.name = 'PaymentRequiredError'
    this.productId = productId
    this.productPrice = productPrice
    this.productName = productName
  }
}

/**
 * Map resume data to interview topics (preview only, no DB writes)
 */
export async function mapResumeForInterview(
  data: ResumeMapRequest
): Promise<ResumeMapResponse> {
  const response = await authenticatedFetch(`${API_BASE_URL}/resumes/map`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  })
  
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Session expired. Please sign in again.')
    }
    if (response.status >= 500) {
      throw new Error('Something went wrong. Please try again later.')
    }
    const errorData = await response.json().catch(() => ({ detail: 'Failed to map resume data' }))
    const backendError = errorData.detail || `Failed to map resume data: ${response.status}`
    throw new Error(getUserFriendlyErrorMessage(backendError, response.status))
  }
  
  return await response.json()
}

/**
 * Save resume data for interview creation
 * Supports both raw data mode and pre-mapped data mode
 */
export async function saveResumeForInterview(
  data: ResumeSaveRequest
): Promise<ResumeSaveResponse> {
  const response = await authenticatedFetch(`${API_BASE_URL}/resumes/save`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  })
  
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Session expired. Please sign in again.')
    }
    if (response.status === 403) {
      const errorData = await response.json().catch(() => ({}))
      const productId = errorData.product_id
      const productPrice = errorData.product_price
      const productName = errorData.product_name
      if (productId && productPrice !== undefined && productName) {
        throw new PaymentRequiredError(productId, productPrice, productName, errorData.detail || 'Payment is required to access Skill Assessment.')
      }
      throw new Error(errorData.detail || 'Payment is required to access Skill Assessment.')
    }
    if (response.status === 409) {
      const errorData = await response.json().catch(() => ({ detail: 'Active interview exists' }))
      const backendError = errorData.detail || 'You already have an interview in progress.'
      throw new Error(getUserFriendlyErrorMessage(backendError, 409))
    }
    if (response.status >= 500) {
      throw new Error('Something went wrong. Please try again later.')
    }
    const errorData = await response.json().catch(() => ({ detail: 'Failed to save resume data' }))
    const backendError = errorData.detail || `Failed to save resume data: ${response.status}`
    throw new Error(getUserFriendlyErrorMessage(backendError, response.status))
  }
  
  return await response.json()
}

/**
 * Get the download URL for the current user's resume file.
 * Use with window.open() for viewing/downloading. Token is included for auth.
 */
export function getResumeDownloadUrl(): string {
  const token = getAuthToken()
  return `${API_BASE_URL}/resumes/me/download?token=${encodeURIComponent(token || '')}`
}

/**
 * Fetch the current user's resume file and return its SHA-256 hash (hex).
 * Returns null if the user has no resume or the request fails.
 * Intended for use only when isResumeFileWhitelistEnforced() is true (see resumeWhitelistValidator).
 */
export async function getCurrentResumeFileHash(): Promise<string | null> {
  const url = getResumeDownloadUrl()
  const response = await fetch(url)
  if (!response.ok) return null
  const buffer = await response.arrayBuffer()
  return generateHashFromArrayBuffer(buffer)
}

export interface InterviewOption {
  option_id: string
  title: string
  role: string
  experience: string
  topics: string[]
  description?: string | null
  is_direct: boolean
}

export interface ResumeOptionsResponse {
  options: InterviewOption[]
}

const _resumeOptionsFetcher = createCoalescedFetcher<ResumeOptionsResponse>(0)

/**
 * Get interview options for the current user's resume.
 * Coalesces concurrent duplicate calls; no caching so a later load stays fresh.
 */
export function getResumeInterviewOptions(): Promise<ResumeOptionsResponse> {
  return _resumeOptionsFetcher('me-options', _getResumeInterviewOptionsImpl)
}

async function _getResumeInterviewOptionsImpl(): Promise<ResumeOptionsResponse> {
  const response = await authenticatedFetch(`${API_BASE_URL}/resumes/me/options`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' }
  })
  
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Session expired. Please sign in again.')
    }
    if (response.status >= 500) {
      throw new Error('Something went wrong. Please try again later.')
    }
    const errorData = await response.json().catch(() => ({ detail: 'Failed to get interview options' }))
    const backendError = errorData.detail || `Failed to get interview options: ${response.status}`
    throw new Error(getUserFriendlyErrorMessage(backendError, response.status))
  }
  
  return await response.json()
}
